<template>
  <div
    class="main-menu"
    :class="[classes, { 'mobile-open': mobileMenuIsOpen }]"
  >
    <ul class="main-nav">
      <!-- Location Selector -->
      <div class="main-menu-section">
        <div
          class="context-switcher"
          @click="openLocationSelectorModal()"
        >
          <Avatar
            icon="map-marker"
            size="sm"
          />
          <template v-if="state !== 'collapsed'">
            <span class="flex-1 text-sm">{{ dealershipDisplayText }}</span>
            <Icon
              type="angles-up-down"
              class="text-xs text-quaternary"
            />
          </template>
        </div>
      </div>

      <!-- Search -->
      <div class="main-menu-section">
        <div
          :class="
            state === 'lockedOpen' || isMobile
              ? 'mx-0 bg-secondary flex items-center justify-between border border-secondary rounded-lg my-1 !py-1 px-3 cursor-pointer flex-nowrap overflow-hidden'
              : 'flex items-center justify-center p-2 px-3 border border-transparent cursor-pointer'
          "
          class="main-nav-item flex justify-center"
          @click="toggleSearchPanel('open')"
        >
          <div class="flex items-center gap-2">
            <Icon
              type="magnifying-glass"
              class="!mr-0"
            />
            <span
              v-if="state === 'lockedOpen' || isMobile"
              class="text-quaternary"
            >
              Search
            </span>
          </div>
          <span
            v-if="state === 'lockedOpen' || isMobile"
            class="text-xs py-0.5 px-1.5 text-secondary border border-secondary rounded-md ml-2"
          >
            <span v-if="isMac">⌘</span>
            <span v-else>Ctrl</span>
            K
          </span>
        </div>
      </div>

      <!-- Navigation Items -->
      <template
        v-for="(section, sectionIndex) in navItems"
        :key="sectionIndex"
      >
        <div
          class="main-menu-section"
          :class="{ 'flex-1 !border-b-0': sectionIndex === 0 }"
        >
          <template
            v-for="(item, itemIndex) in section"
            :key="item.route || itemIndex"
          >
            <li
              class="main-nav-item"
              :class="{
                'has-sub': item.children,
                'has-active-child': state === 'collapsed' && hasActiveChild(item),
                'router-link-active': !item.children && isRouteActive(item.route),
                open:
                  item.isOpen ||
                  $route.matched.some(route => route.path === item.route || route.path.startsWith(item.route + '/'))
              }"
              @click="!item.children && handleNavigation(item.route)"
            >
              <!-- When collapsed, show Dropdown for all items -->
              <template v-if="state === 'collapsed'">
                <!-- Items with children -->
                <Dropdown
                  v-if="item.children"
                  position="right-start"
                  :close-on-click-within="true"
                  :close-on-click-away="false"
                  :trigger-on-hover="true"
                  :space="15"
                >
                  <template #triggerContent>
                    <Icon :type="item.icon" />
                  </template>
                  <div class="nav-item-tooltip">
                    {{ $t(item.localeKey, item.localePluralization) }}
                  </div>
                  <DropdownSeparator />
                  <template
                    v-for="childItem in item.children"
                    :key="childItem.route"
                  >
                    <DropdownItem
                      v-if="!childItem.children && canAccess(childItem)"
                      :route="childItem.route"
                    >
                      <!-- <Icon type="circle" /> -->
                      <span>{{ $t(childItem.localeKey, childItem.localePluralization) }}</span>
                    </DropdownItem>
                    <DropdownItem
                      v-else-if="childItem.children && canAccess(childItem)"
                      :has-sub-menu="true"
                      :close-on-click="false"
                    >
                      <!-- <Icon type="circle" /> -->
                      <span>{{ $t(childItem.localeKey, childItem.localePluralization) }}</span>
                      <template #submenu>
                        <DropdownItem
                          v-for="childItem2 in childItem.children.filter(c => canAccess(c))"
                          :key="childItem2.route"
                          :route="childItem2.route"
                          :close-on-click="false"
                        >
                          <!-- <Icon type="circle" /> -->
                          <span>{{ $t(childItem2.localeKey, childItem2.localePluralization) }}</span>
                        </DropdownItem>
                      </template>
                    </DropdownItem>
                  </template>
                </Dropdown>
                <!-- Items without children - show tooltip on hover -->
                <Dropdown
                  v-else
                  position="right-start"
                  :trigger-on-hover="true"
                  :close-on-click-away="false"
                  :padding="false"
                  :space="15"
                >
                  <template #triggerContent>
                    <div @click.stop="handleNavigation(item.route)">
                      <Icon :type="item.icon" />
                    </div>
                  </template>
                  <div class="nav-item-tooltip">
                    {{ $t(item.localeKey, item.localePluralization) }}
                  </div>
                </Dropdown>
              </template>
              <!-- When not collapsed, use normal rendering -->
              <template v-else>
                <!-- If Item does not have children - Level 1 -->
                <div
                  v-if="!item.children"
                  class="main-nav-item-content"
                >
                  <Icon :type="item.icon" />
                  <span class="main-nav-item-title">
                    {{ $t(item.localeKey, item.localePluralization) }}
                    <Badge
                      v-if="
                        state !== 'collapsed' &&
                        item.id === 'tasks' &&
                        taskStore.outstandingTasks !== null &&
                        taskStore.outstandingTasks > 0
                      "
                      rounded
                      inline
                      size="sm"
                      :content="taskStore.outstandingTasks"
                    ></Badge>
                  </span>
                </div>
                <!-- ELSE if item does have children - Level 2-->
                <div
                  v-else
                  class="main-nav-item-content"
                  @click.stop="subMenuToggle(item)"
                >
                  <Icon :type="item.icon" />
                  <span class="main-nav-item-title">{{ $t(item.localeKey, item.localePluralization) }}</span>
                </div>
              </template>
            </li>
            <!-- Children rendered below parent as sibling list items -->
            <template v-if="state !== 'collapsed' && item.children">
              <li
                v-for="childItem in item.children"
                :key="childItem.route"
                :class="{
                  'has-sub': childItem.children,
                  'main-nav-child': true,
                  'router-link-active': !childItem.children && isRouteActive(childItem.route),
                  'child-visible':
                    item.isOpen ||
                    $route.matched.some(route => route.path === item.route || route.path.startsWith(item.route + '/')),
                  open:
                    childItem.isOpen ||
                    item.isOpen ||
                    $route.matched.some(
                      route => route.path === childItem.route || route.path.startsWith(childItem.route + '/')
                    )
                }"
                class="main-nav-item"
                @click="!childItem.children && handleNavigation(childItem.route)"
              >
                <!-- If childItem does not have children -->
                <div
                  v-if="!childItem.children"
                  class="main-nav-item-content"
                >
                  <Icon type="circle" />
                  <span class="main-nav-item-title">
                    {{ $t(childItem.localeKey, childItem.localePluralization) }}
                  </span>
                </div>
                <!-- ELSE if childItem does have children - Level 3-->
                <div
                  v-else
                  class="main-nav-item-content"
                  @click.stop="subMenuToggle(childItem)"
                >
                  <Icon type="circle" />
                  <span class="main-nav-item-title">
                    {{ $t(childItem.localeKey, childItem.localePluralization) }}
                  </span>
                </div>
                <ul
                  v-if="childItem.children"
                  class="main-nav-submenu"
                >
                  <template
                    v-for="childItem2 in childItem.children.filter(c => canAccess(c))"
                    :key="childItem2.route"
                  >
                    <li
                      class="main-nav-item"
                      :class="{ 'router-link-active': isRouteActive(childItem2.route) }"
                      @click="handleNavigation(childItem2.route)"
                    >
                      <!-- Last level. No more possible -->
                      <div class="main-nav-item-content">
                        <Icon type="circle" />
                        <span class="main-nav-item-title">
                          {{ $t(childItem2.localeKey, childItem2.localePluralization) }}
                        </span>
                      </div>
                    </li>
                  </template>
                </ul>
              </li>
            </template>
          </template>
        </div>
      </template>
    </ul>

    <div class="main-menu-section !border-b-0 flex flex-col expand-button-section">
      <div
        v-if="!isMobile"
        class="expand-button-wrapper"
      >
        <button
          v-tippy="state === 'lockedOpen' ? 'Collapse Sidebar' : 'Expand Sidebar'"
          class="expand-button"
          @click="toggleMenuLock"
        >
          <div class="text-xs">
            <Icon
              v-if="state === 'lockedOpen'"
              type="chevron-left"
            />
            <Icon
              v-else
              type="chevron-right"
            />
          </div>
        </button>
      </div>
      <!-- User Avatar Dropdown -->
      <Dropdown
        :key="`user-dropdown-${isMobile}`"
        :position="isMobile ? 'top-start' : 'right-start'"
        :space="isMobile ? 10 : undefined"
        :close-on-click-within="true"
      >
        <template #triggerContent>
          <div class="context-switcher">
            <Avatar
              size="sm"
              :url="authStore.details.profilePicUrl"
              :user-presence-user-id="authStore.details.id"
              :text="authStore.details.initials"
            />
            <template v-if="state !== 'collapsed'">
              <span class="text-primary flex-1">{{ authStore.details.name }}</span>
              <Icon
                type="angles-up-down"
                class="text-xs text-quaternary"
              />
            </template>
          </div>
        </template>
        <DropdownItem :route="`/settings/users/${authStore.details.id}`">
          <AvatarLabelGroup
            :label="authStore.details.name"
            :description="authStore.details.email"
            :avatar-url="authStore.details.profilePicUrl"
            :avatar-text="authStore.details.initials"
          />
        </DropdownItem>

        <DropdownSeparator />

        <!-- <DropdownItem route="/me/profile">
              <Icon type="user" />
              <span class="w-full">{{ $t('ui.entities.profile.label') }}</span>
            </DropdownItem>-->
        <DropdownItem :route="`/settings/users/${authStore.details.id}`">
          <Icon type="cog" />
          <span class="w-full">{{ $t('ui.entities.setting', 2) }}</span>
        </DropdownItem>

        <DropdownSeparator />

        <!-- INFO: These use the a href link as canny requires the 'data-canny-link' attribute to be on the anchor tag -->
        <!-- <a data-canny-link href="https://retain-automotive.canny.io/changelog">
              <DropdownItem v-if="authStore.details.userType == 1">
                <Icon type="sparkles" />
                <span class="w-full">{{ $t('ui.support.whatsNew') }}</span>
              </DropdownItem>
            </a> -->

        <a
          data-canny-link
          href="https://retain-automotive.canny.io/feature-requests"
        >
          <DropdownItem v-if="authStore.details.userType == 1">
            <Icon type="lightbulb-on" />
            <span class="w-full">{{ $t('ui.support.featureRequests') }}</span>
          </DropdownItem>
        </a>

        <a
          data-canny-link
          href="https://retain-automotive.canny.io/bugs-issues"
        >
          <DropdownItem v-if="authStore.details.userType == 1">
            <Icon type="bug" />
            <span class="w-full">{{ $t('ui.support.reportIssue') }}</span>
          </DropdownItem>
        </a>

        <DropdownItem link="mailto:support@retain.group">
          <Icon type="ticket" />
          <span class="w-full">{{ $t('ui.support.label') }}</span>
        </DropdownItem>

        <div class="text-xs text-quaternary ml-2 my-1">App Version: {{ appVersion }}</div>

        <DropdownSeparator />

        <!-- Language Selector -->
        <DropdownItem :has-sub-menu="true">
          <Icon type="language" />
          <span class="w-full">{{ currentLanguageLabel }}</span>
          <template #submenu>
            <DropdownItem
              v-for="lang in languageOptions"
              :key="lang.value"
              @item-clicked="changeLocale(lang.value)"
            >
              <span>{{ lang.label }}</span>
            </DropdownItem>
          </template>
        </DropdownItem>

        <DropdownSeparator />

        <!-- Dark Mode Selector -->
        <div class="px-3 py-2">
          <RadioTabbed
            v-model="selectedTheme"
            :options="systemThemeOptions"
            full-width
            @option-change="darkmodeChange"
          />
        </div>

        <DropdownSeparator />

        <DropdownItem @item-clicked="logout">
          <Icon type="arrow-right-from-bracket" />
          <span class="w-full">{{ $t('ui.common.signOut') }}</span>
        </DropdownItem>
      </Dropdown>
      <!-- <div
        v-tippy="{ content: env }"
        class="my-3 text-xs"
      >
        {{ appVersion }}
      </div> -->
    </div>
  </div>
  <div
    v-if="isMobile && mobileMenuIsOpen"
    class="main-menu-overlay"
    @click="closeMobileMenu"
  ></div>
  <GlobalSearch
    :is-open="searchIsOpen"
    @close-search="closeSearch"
  />
  <!-- Location Selector Modal -->
  <Modal
    :is-open="locationSelectorModalIsOpen"
    :title="$t('ui.entities.locationSelection.label')"
    :buttons="[]"
    :show-dismiss-button="true"
    :max-width="600"
    @close-modal="closeLocationSelectorModal()"
  >
    <LocationSelector @save="handleLocationSave" />
  </Modal>
</template>

<script setup>
import { state, mobileMenuIsOpen } from './state'
import { ref, watch, onMounted, computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
import { useUserSettingsStore } from '@/stores/UserSettingsStore.ts'
import { useTaskStore } from '@/stores/TaskStore.ts'
import { useAccessStore } from '@/stores/AccessStore.ts'
import { useAbility } from '@casl/vue'

// composables
import { useNavItems } from '@/composables/useNavigation.ts'
import { useSearch } from '@/composables/useSearch.ts'
import { useLocationSelector } from '@/composables/useLocationSelector.ts'

// components
import Badge from '@/components/badge/Badge.vue'
import Icon from '@/components/icon/Icon.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import AvatarLabelGroup from '@/components/avatar/AvatarLabelGroup.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import DropdownSeparator from '@/components/dropdown/DropdownSeparator.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import GlobalSearch from '@/components/search/GlobalSearch.vue'
import Modal from '@/components/modal/Modal.vue'
import LocationSelector from '@/components/main-menu/LocationSelector.vue'
import packageInfo from '../../../package.json'

const navItems = useNavItems()
const { searchIsOpen, isMac, toggleSearchPanel, closeSearch } = useSearch()
const { locationSelectorModalIsOpen, openLocationSelectorModal, closeLocationSelectorModal } = useLocationSelector()

const props = defineProps({
  isMobile: {
    type: Boolean,
    default: false
  }
})
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const userSettingsStore = useUserSettingsStore()
const taskStore = useTaskStore()
const accessStore = useAccessStore()
const classes = ref('')

const { can } = useAbility()

const appVersion = computed(() => {
  return packageInfo.version
})

/**
 * Computed property to get the number of dealerships selected by the user
 * @returns {number} The count of selected dealerships
 */
const dealershipCount = computed(() => {
  return accessStore.selectedDealershipCount
})

/**
 * Computed property to get the display text for selected dealerships
 * Shows the dealership name if only 1 is selected, otherwise shows the count
 * @returns {string} The display text for selected dealerships
 */
const dealershipDisplayText = computed(() => {
  const count = dealershipCount.value
  if (count === 1) {
    // Get the single selected dealership ID
    const selectedId = accessStore.accessSelected.dealerships[0]
    // Find the dealership in accessAllowed to get its name
    const dealership = accessStore.accessAllowed.dealerships.find(d => d.id === selectedId)
    return dealership?.name || '1 Location'
  } else {
    return `${count} Locations`
  }
})

// Language selector
const localeIso = ref(localStorage.getItem('locale'))
const languageOptions = [
  { label: 'English (UK)', value: 'en-GB' },
  { label: 'English (US)', value: 'en-US' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' }
]

const currentLanguageLabel = computed(() => {
  const currentLang = languageOptions.find(lang => lang.value === localeIso.value)
  return currentLang ? currentLang.label : 'Language'
})

function changeLocale(iso) {
  if (localeIso.value !== iso) {
    localeIso.value = iso
    localStorage.setItem('locale', iso)
    location.reload()
  }
}

// Dark mode selector
const selectedTheme = computed(() => userSettingsStore.userSettings.darkmode)
const systemThemeOptions = [
  {
    label: 'Light',
    value: 'light',
    icon: 'sun-bright'
  },
  {
    label: 'Dark',
    value: 'dark',
    icon: 'moon'
  },
  {
    label: 'Auto',
    value: 'auto',
    icon: 'eclipse'
  }
]

function darkmodeChange(option) {
  userSettingsStore.updateUserSettings([
    {
      name: 'darkmode',
      value: option
    }
  ])
}

function logout() {
  authStore.signOut()
}

function setClasses() {
  // console.log('toggle mobile func')
  if (props.isMobile) {
    // console.log('Is Mobile')
    classes.value = 'expanded mobile'
    state.value = 'expanded'
  } else {
    // console.log('Is Desktop/Tablet')
    if (state.value === 'lockedOpen') {
      classes.value = 'lockedOpen expanded'
    }
    if (state.value === 'collapsed') {
      classes.value = 'collapsed'
    }
  }
}

const canAccess = item => {
  // If canGate or rolesAllowed is undefined, return true
  if (item.canGate === undefined) {
    return true
  } else {
    return can(item.canGate)
  }
}

/**
 * Checks if any child route of the given item is currently active
 * @param {Object} item - The nav item to check
 * @returns {boolean} - True if any child route is active
 */
const hasActiveChild = item => {
  if (!item.children) {
    return false
  }

  return item.children.some(child => {
    // Check if the child route itself is active
    const childIsActive = route.matched.some(
      matchedRoute => matchedRoute.path === child.route || matchedRoute.path.startsWith(child.route + '/')
    )

    // Also check if any grandchild is active (recursive)
    if (child.children) {
      return childIsActive || hasActiveChild(child)
    }

    return childIsActive
  })
}

/**
 * Recursively expands parent nav items if any of their children are active
 * @param {Array} items - Array of nav items to check
 */
const autoExpandActiveParents = items => {
  if (!items || items.length === 0) {
    return
  }

  items.forEach(item => {
    if (item.children) {
      // Check if any child is active
      const hasActive = hasActiveChild(item)

      if (hasActive) {
        // Set parent to open if it has an active child
        item.isOpen = true

        // Recursively check children (for 3-level deep menus)
        autoExpandActiveParents(item.children)
      }
    }
  })
}

// If menu state is 'expanded' (from old hover behavior), convert to 'collapsed'
// Only 'collapsed' and 'lockedOpen' states are valid now
if (state.value === 'expanded') {
  state.value = 'collapsed'
}
function toggleMenuLock() {
  if (state.value === 'lockedOpen') {
    state.value = 'collapsed'
  } else {
    state.value = 'lockedOpen'
  }
  setClasses()
}

/**
 * Checks if a route is currently active
 * @param {string} routePath - The route path to check
 * @returns {boolean} - True if the route is active
 */
function isRouteActive(routePath) {
  if (!routePath) return false

  const currentPath = route.path

  // Exact match
  if (currentPath === routePath) {
    return true
  }

  // Check if current path is a child of routePath
  // But only if there's no more specific route that also matches
  if (currentPath.startsWith(routePath + '/')) {
    // Collect all possible routes from nav items to check for more specific matches
    const allRoutes = []
    navItems.forEach(section => {
      section.forEach(item => {
        if (item.children) {
          item.children.forEach(child => {
            if (child.route && child.route !== routePath) {
              allRoutes.push(child.route)
            }
          })
        } else if (item.route && item.route !== routePath) {
          allRoutes.push(item.route)
        }
      })
    })

    // Check if there's a more specific route (child of routePath) that matches currentPath
    // If so, don't mark the parent routePath as active
    const hasMoreSpecificChild = allRoutes.some(otherRoute => {
      // Check if otherRoute is a child of routePath and matches currentPath
      return (
        otherRoute.startsWith(routePath + '/') &&
        (currentPath === otherRoute || currentPath.startsWith(otherRoute + '/'))
      )
    })

    // Only mark as active if there's no more specific child route that matches
    return !hasMoreSpecificChild
  }

  return false
}

/**
 * Handles saving location selections and closes the modal
 */
function handleLocationSave() {
  closeLocationSelectorModal()
  // The dealershipCount computed property will automatically update
  // since it reads from localStorage which was updated in LocationSelector
}

/**
 * Handles navigation for nav items without children
 * @param {string} route - The route to navigate to
 */
function handleNavigation(route) {
  if (route) {
    router.push(route)
    // Close mobile menu after navigation
    mobileMenuIsOpen.value = false
  }
}

/**
 * Toggles submenu for nav items with children
 * @param {Object} item - The nav item to toggle
 */
function subMenuToggle(item) {
  // console.log('Toggled item: ' + item.title);

  item.isOpen = !item.isOpen
}

function closeMobileMenu() {
  mobileMenuIsOpen.value = false
}

onMounted(() => {
  setClasses()

  // Auto-expand parents with active children when menu is expanded
  if (state.value !== 'collapsed') {
    navItems.forEach(section => {
      autoExpandActiveParents(section)
    })
  }

  // Listen for event bus event to open location selector modal
  const emitter = inject('emitter')
  if (emitter) {
    emitter.on('open-location-selector-modal', () => {
      openLocationSelectorModal()
    })
  }
})

watch(
  () => route.name,
  (currentRoute, newRoute) => {
    if (currentRoute !== newRoute) {
      // console.log(route)

      // close mobile menu
      mobileMenuIsOpen.value = false

      // Close all menus first
      navItems.forEach(section => {
        section.forEach(item => {
          item.isOpen = false
        })
      })

      // Auto-expand parents with active children when menu is expanded
      if (state.value !== 'collapsed') {
        navItems.forEach(section => {
          autoExpandActiveParents(section)
        })
      }
    }
  }
)

watch(
  () => state.value,
  newState => {
    localStorage.setItem('mainMenuState', newState)

    // Auto-expand parents with active children when menu becomes expanded
    if (newState !== 'collapsed') {
      navItems.forEach(section => {
        autoExpandActiveParents(section)
      })
    }
  }
)

// Watch route path changes to auto-expand active parents
watch(
  () => route.path,
  () => {
    // Auto-expand parents with active children when menu is expanded
    if (state.value !== 'collapsed') {
      navItems.forEach(section => {
        autoExpandActiveParents(section)
      })
    }
  }
)

watch(
  () => props.isMobile,
  (newVal, oldVal) => {
    if (oldVal) {
      // If was mobile, but changing to desktop
      state.value = 'collapsed'
      // Close mobile menu when switching to desktop
      mobileMenuIsOpen.value = false
    }
    // console.log('Prop changed: ', newVal, ' | was: ', oldVal)
    setClasses()
  }
)
</script>

<style>
/* MainMenu.vue */
.main-menu {
  color: var(--text-primary);
  background-color: var(--bg-primary_alt);

  border-top: 1px solid var(--border-secondary);
  border-right: 1px solid var(--border-secondary);
  border-bottom: 1px solid var(--border-secondary);

  border-radius: 0 var(--rounded-2xl) var(--rounded-2xl) 0;
  /* border-radius: var(--rounded-2xl); */
  /* box-shadow: 0 0 10px 0 rgb(34 41 47 / 15%); */
  box-shadow: 0 0 10px 0 rgb(34 41 47 / 8%);

  float: left;
  position: fixed;
  z-index: 105;
  top: 20px;
  left: 0;
  bottom: 20px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.main-menu.expanded {
  width: 220px;
}

.main-menu.collapsed {
  width: 60px;
}

.main-menu.mobile {
  transform: translate(-100%);
  width: 100%;
  max-width: 100%;
  border-radius: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  pointer-events: none;
}

.main-menu.mobile.mobile-open {
  transform: none;
  z-index: 110;
  pointer-events: auto;
}

.main-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(34, 41, 47, 0.5);
  transition: all 0.3s ease-in-out;
  -webkit-transition: all 0.3s ease-in-out;
  z-index: 109;
}

.main-menu-section {
  padding: var(--s-2) 0.6rem;
  border-bottom: 1px solid var(--border-secondary);
}

.expand-button-section {
  overflow: visible;
  position: relative;
}

.main-nav {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.main-nav-section {
  display: flex;
  flex-direction: column;
  margin: 0;
}

.main-nav-item {
  position: relative;
  white-space: nowrap;
  font-size: var(--text-md);
  font-weight: 500;
  text-transform: capitalize;
  padding: var(--s-2) var(--s-3);
  color: var(--text-primary);
  border-radius: var(--rounded-md);
  cursor: pointer;
}

.main-nav-item.has-sub > .main-nav-item-content::after {
  font-family: 'Font Awesome 7 Duotone';
  content: '\f107';
  position: absolute;
  right: 12px;
  top: 13px;
  font-size: 0.8rem;
  color: var(--fg-quinari);
  transition: transform 0.25s ease-in-out;
}

.main-nav-item-content {
  display: flex;
  align-items: center;
}

.main-nav-item-content > * {
  transition: transform 0.25s ease-in-out;
}

.main-nav-item-title {
  display: inline-flex;
  align-items: center;
}

.main-nav-item .icon {
  margin-right: var(--s-3);
  flex-shrink: 0;
  font-size: 1.2rem;
}

/* If has children */
.main-nav-item.has-sub .main-nav-submenu {
  display: none;
  margin-top: 5px;
}

.main-nav-item.has-sub .main-nav-item > .main-nav-item-content {
  padding-left: 35px;
  background: none;
}

.main-nav-item.has-sub .main-nav-item .icon {
  font-size: 0.5rem;
}

.main-nav-item:hover {
  background: var(--bg-tertiary) !important;
}

.main-nav-item.open > .main-nav-item-content::after {
  transform: rotate(-180deg);
}

.main-nav-item.open > .main-nav-submenu {
  display: block;
}

/* Child items rendered below parent */
.main-nav-child {
  display: none;
  padding-left: 35px;
}

.main-nav-child.child-visible {
  display: block;
}

.main-nav-child > .main-nav-item-content {
  padding-left: 0;
}

.main-nav-child .icon {
  font-size: 0.5rem;
}

/* Router & active menu items */
.main-nav-item:has(.router-link-exact-active),
.main-nav-item.router-link-active {
  background: var(--bg-brand-light) !important;
  color: var(--text-brand);
}

.main-nav-item:has(.router-link-exact-active) > .main-nav-item-content,
.main-nav-item.router-link-active > .main-nav-item-content {
  color: var(--text-brand);
}

/* styles when collapsed */
.main-menu.collapsed .main-nav-submenu {
  display: none;
}

.main-menu.collapsed .main-nav-child {
  display: none !important;
}

.main-menu.collapsed .main-nav-item.has-sub > a::after {
  display: none;
}

.main-menu.collapsed .main-nav-item-title {
  display: none;
}

.main-menu.collapsed .main-nav-item:has(.router-link-exact-active),
.main-menu.collapsed .main-nav-item.has-active-child {
  background: var(--bg-tertiary) !important;
  color: var(--text-brand);
}

.main-menu.collapsed .main-nav-item:has(.router-link-exact-active) > .main-nav-item-content,
.main-menu.collapsed .main-nav-item.router-link-active > .main-nav-item-content,
.main-menu.collapsed .main-nav-item.has-active-child > .main-nav-item-content,
.main-menu.collapsed .main-nav-item.has-active-child > div,
.main-menu.collapsed .main-nav-item.has-active-child .icon {
  color: var(--text-brand);
}

.dark .main-menu.collapsed .main-nav-item:has(.router-link-exact-active),
.dark .main-menu.collapsed .main-nav-item.has-active-child {
  background: var(--gray-800);
}

.collapsed .main-nav-item .badge {
  transform: translate(-13px, 0px);
  font-size: 0.7rem;
  min-width: 20px;
}

/* Nav item tooltip styling */
.nav-item-tooltip {
  padding: var(--s-1-5) var(--s-3);
  font-size: 0.75rem;
  color: var(--text-secondary);
  white-space: nowrap;
  font-weight: 500;
}

.context-switcher {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-2) var(--s-2);
  border-radius: var(--rounded-md);
  cursor: pointer;
  width: 100%;
  color: var(--text-primary);
}
.context-switcher:hover {
  background: var(--bg-tertiary);
}

.expand-button-wrapper {
  position: absolute;
  right: 0;
  bottom: 44px;
  transform: translateX(50%);
  display: flex;
  z-index: 106;
  pointer-events: auto;
}

.expand-button {
  padding: var(--s-1);
  border-radius: var(--rounded-md);
  cursor: pointer;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  pointer-events: auto;
  position: relative;
  z-index: 106;
}

.expand-button:hover {
  background: var(--bg-tertiary);
}

.dark .expand-button:hover {
  background: var(--gray-800);
}
</style>
